import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../api/client';
import KonspektTiptapEditor from '../components/KonspektTiptapEditor';
import TagTokenInput from '../components/TagTokenInput';

export default function KonspektFormPage() {
  const { slug } = useParams();
  const isEdit = Boolean(slug);
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [docJson, setDocJson] = useState(() =>
    isEdit ? null : { type: 'doc', content: [{ type: 'paragraph' }] },
  );
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr('');
      try {
        const { data } = await api.get(`/materials/konspekty/${encodeURIComponent(slug)}/`);
        if (cancelled) return;
        setTitle(data.title || '');
        setSelectedTags(
          (data.tags || []).map((t) => ({
            id: t.id,
            slug: t.slug,
            name: t.name,
            type: t.type || '',
          })),
        );
        setDocJson(data.content?.tiptap || { type: 'doc', content: [{ type: 'paragraph' }] });
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.detail || 'Не удалось загрузить конспект');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, slug]);

  const onDocJsonChange = useCallback((json) => {
    setDocJson(json);
  }, []);

  const onSelectedTagsChange = useCallback((tags) => {
    setSelectedTags(tags);
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setErr('');
    const t = title.trim();
    if (!t) {
      setErr('Укажите заголовок');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: t,
        tag_ids: selectedTags.map((x) => x.id),
        content: { tiptap: docJson || { type: 'doc', content: [{ type: 'paragraph' }] } },
      };
      if (isEdit) {
        const { data } = await api.patch(`/materials/konspekty/${encodeURIComponent(slug)}/`, payload);
        navigate(`/konspekty/${encodeURIComponent(data.slug)}`);
      } else {
        const { data } = await api.post('/materials/konspekty/', payload);
        navigate(`/konspekty/${encodeURIComponent(data.slug)}`);
      }
    } catch (ex) {
      const d = ex.response?.data;
      setErr(typeof d === 'string' ? d : d?.detail || JSON.stringify(d) || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  }

  if (loading || docJson === null) {
    return (
      <div className="page">
        <p className="muted">Загрузка…</p>
      </div>
    );
  }

  return (
    <div className="page konspekt-form-page">
      <div className="konspekt-form-head">
        <Link to={isEdit ? `/konspekty/${encodeURIComponent(slug)}` : '/konspekty'} className="konspekt-back">
          ← Назад
        </Link>
        <h1>{isEdit ? 'Редактирование' : 'Новый конспект'}</h1>
      </div>

      <form onSubmit={handleSubmit} className="konspekt-form">
        {err && <p className="form-error">{err}</p>}
        <label className="form-label">
          Заголовок
          <input className="form-input" value={title} onChange={(e) => setTitle(e.target.value)} required maxLength={300} />
        </label>

        <fieldset className="konspekt-tags-fieldset">
          <legend>Теги</legend>
          <TagTokenInput
            value={selectedTags}
            onChange={onSelectedTagsChange}
            showTypeFilter
            label="Теги"
            placeholder="Введите часть названия тега или выберите тип ниже"
          />
        </fieldset>

        <div className="konspekt-editor-block">
          <div className="form-label">Текст</div>
          <KonspektTiptapEditor key={isEdit ? slug : 'new'} initialDocJson={docJson} onDocJsonChange={onDocJsonChange} />
        </div>

        <div className="konspekt-form-actions">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Сохранение…' : 'Сохранить'}
          </button>
        </div>
      </form>
    </div>
  );
}
